import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UntypedFormControl, Validators } from '@angular/forms';
import { SelectOption } from '@df/design-system-components';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { Analyzer } from 'src/app/storage/models/analyzer.model';
import { Mission } from 'src/app/storage/models/mission.model';
import { ITrain } from 'src/app/storage/interfaces/train.interface';

export interface AnalyzerSelection {
  analyzer: Analyzer;
  trainpk: number;
  handleFlyingH2?: boolean;
}

@Component({
    selector: 'app-add-analyzer',
    templateUrl: './add-analyzer.component.html',
    styleUrls: ['./add-analyzer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddAnalyzerComponent implements OnInit, OnDestroy {
  analyzers: Analyzer[] = [];
  displayedAnalyzers: Analyzer[] = [];

  @Input() mission: Mission;
  @Input() missionTrains: ITrain[] = [];
  @Output() closeModal = new EventEmitter<AnalyzerSelection>();

  selectedTrainFC = new UntypedFormControl(null, Validators.required);
  trainOptions: SelectOption<number>[] = [];
  showTrainSelector = true;

  private analyzersSubscription: Subscription;
  private trainSubscription: Subscription;
  private searchValue = '';

  constructor(
    private analyzersService: AnalyzersService,
    private confirmationModalService: ConfirmationModalService,
    private translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    const availableTrains = this.missionTrains ?? [];
    this.trainOptions = availableTrains.map(train => ({
      value: train.trainpk,
      label: train.trainName || train.trainId
    }));
    const onlyDefaultTrain = availableTrains.length === 1
      && availableTrains[0].trainId?.trim().toUpperCase() === 'DEFAULT';
    this.showTrainSelector = !onlyDefaultTrain;
    if (onlyDefaultTrain) {
      this.selectedTrainFC.setValue(availableTrains[0].trainpk, {emitEvent: false});
    }
    this.analyzersSubscription = this.analyzersService.analyzers$.subscribe(() => this._refreshAvailableAnalyzers());
    this.trainSubscription = this.selectedTrainFC.valueChanges.subscribe(() => this._refreshAvailableAnalyzers());
  }

  ngOnDestroy(): void {
    if (this.analyzersSubscription) { this.analyzersSubscription.unsubscribe(); }
    if (this.trainSubscription) { this.trainSubscription.unsubscribe(); }
  }

  close(): void {
    this.confirmationModalService.closeModal();
    this.closeModal.emit();
  }

  addAnalyzer(analyzer: Analyzer): void {
    const trainpk = Number(this.selectedTrainFC.value);
    if (!Number.isFinite(trainpk) || trainpk <= 0) { return; }
    if (this.shouldAskHandleFlyingH2(analyzer)) {
      this.openHandleFlyingH2Modal(analyzer, trainpk);
      return;
    }

    this.closeModal.emit({ analyzer, trainpk });
  }

  private shouldAskHandleFlyingH2(analyzer: Analyzer): boolean {
    const modelName = (analyzer.analyzermodelName ?? '').toLowerCase();
    const isMetler = modelName.includes('metler') || modelName.includes('mettler');
    const hasH2 = (analyzer.analyzermodelGas ?? '').split(' ').find(gas => gas === 'H2') != null;
    return isMetler && hasH2;
  }

  private async openHandleFlyingH2Modal(analyzer: Analyzer, trainpk: number): Promise<void> {
    const translations = await this.translateService.get('MATERIAL.ANALYZER').toPromise();
    const yes = await this.translateService.get('YES').toPromise();
    const no = await this.translateService.get('NO').toPromise();

    this.confirmationModalService.openModal({
      title: `<strong>${translations['HANDLE_FLYING_H2_QUESTION']}</strong>`,
      buttons: [
        {
          text: yes,
          method: async () => {
            this.confirmationModalService.closeModal();
            this.closeModal.emit({ analyzer, trainpk, handleFlyingH2: true });
          }
        },
        {
          text: no,
          method: async () => {
            this.confirmationModalService.closeModal();
            this.closeModal.emit({ analyzer, trainpk });
          }
        }
      ]
    });
  }

  filterAnalyzers(value: string): void {
    this.searchValue = value ?? '';
    this.displayedAnalyzers = this.analyzers.filter(analyzer => {
      // List attribute to filter on
      const attributesToFilterOn = [
        analyzer.analyzerId,
        analyzer.analyzermodelName
      ];

      // Find at least one attribute that checks the criterion
      return attributesToFilterOn.find(attribute =>
        attribute.toUpperCase().includes(this.searchValue.toUpperCase())
      ) != null;
    });
  }

  private _refreshAvailableAnalyzers(): void {
    const analyzers = this.analyzersService.analyzers$.getValue() ?? [];
    const missionAnalyzers = this.analyzersService.missionAnalyzers$.getValue() ?? [];
    const selectedTrainpk = Number(this.selectedTrainFC.value);
    this.analyzers = analyzers
      .filter(analyzer => !missionAnalyzers.some(missionAnalyzer =>
        analyzer.analyzerId === missionAnalyzer.analyzerId
        && missionAnalyzer.trainpk !== selectedTrainpk))
      .filter(analyzer => analyzer.isActivated);
    this.filterAnalyzers(this.searchValue);
  }

}
